import * as bip39 from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
/**
 * Generate a fresh BIP39 mnemonic. Defaults to 128 bits (12 words).
 * Entropy comes from @scure/bip39, which uses WebCrypto / Node crypto.
 */
export function generateMnemonic(strength = 128) {
    return bip39.generateMnemonic(wordlist, strength);
}
/**
 * Validate a BIP39 mnemonic. Accepts messy input (extra whitespace, mixed case)
 * by normalizing first. Returns true only if the checksum is valid AND all
 * words are in the English wordlist.
 */
export function validateMnemonic(mnemonic) {
    const normalized = normalizeMnemonic(mnemonic);
    return bip39.validateMnemonic(normalized, wordlist);
}
/**
 * Derive the 64-byte BIP39 seed from a mnemonic.
 * Uses PBKDF2-HMAC-SHA512 with 2048 iterations per the spec.
 */
export async function mnemonicToSeed(mnemonic, passphrase = '') {
    const normalized = normalizeMnemonic(mnemonic);
    return bip39.mnemonicToSeed(normalized, passphrase);
}
/**
 * Derive the 64-byte BIP39 seed from a mnemonic passed as Uint8Array bytes
 * (UTF-8 encoded). This is the preferred path for the signing flow because
 * it never requires materializing the mnemonic as a JavaScript string —
 * JS strings are immutable and cannot be zeroed, making them a memory
 * residency risk. See spec invariant I2 (§4.3).
 *
 * The caller owns the input bytes and is responsible for zeroing after use.
 * This function does NOT modify the input.
 */
export async function mnemonicBytesToSeed(mnemonicBytes, passphrase = '') {
    // @scure/bip39 only exposes mnemonicToSeed(string), so we create a
    // short-lived intermediate JS string here. JS strings are immutable and
    // cannot be zeroed; the residency window is minimized by keeping the
    // string local to this function so GC can reclaim it soon after return.
    //
    // Future improvement: port PBKDF2-HMAC-SHA512 to operate directly on
    // bytes. For v0.1.0 the short-lived local string is acceptable because
    // it does not cross the signing boundary and is not held by any outer
    // closure.
    const mnemonic = new TextDecoder().decode(mnemonicBytes);
    const normalized = normalizeMnemonic(mnemonic);
    return bip39.mnemonicToSeed(normalized, passphrase);
}
/**
 * Normalize user-entered mnemonic text: trim, lowercase, collapse whitespace.
 * This is idempotent and safe to call on already-normalized input.
 */
export function normalizeMnemonic(mnemonic) {
    return mnemonic.trim().toLowerCase().split(/\s+/).join(' ');
}
//# sourceMappingURL=mnemonic.js.map